import clsx from "clsx";
import { Children, FC, useCallback, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { HoverableToolbarContext } from "./HoverableToolbarProvider";
import { DeleteIconButtonProps, ToolbarDeleteIconButton } from "./ToolbarDeleteIconButton";

import {
  AddIconButtonProps,
  ToolbarAddIconButton,
} from "components/hover-widgets/ToolbarAddIconButton";
import {
  EditIconButtonProps,
  ToolbarEditIconButton,
} from "components/hover-widgets/ToolbarEditIconButton";
import { IconSizeType } from "mds/components/LegacyButton";
import { useHoverable } from "mds/hooks/use-hoverable";
import { useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";
import { usePresentation, useSelectedPageListItemColoring } from "utils/presentation";

type HoverableToolbarProps = {
  alwaysShowAllChildren?: boolean;
  className?: string;
  contentClassName?: string;
  uncentered?: boolean;
  reference?: AddIconButtonProps["reference"] | EditIconButtonProps["reference"];
  readOnly?: boolean;
  label?: string;
  children: React.ReactNode | [React.ReactNode, React.ReactNode];
  showEditButton?: boolean;
  disableUnlessEditMode?: boolean;
  eventPrefix?: string;
  onClickAdd?: () => void;
  onClickDelete?: () => void;
  onEditModeChange?: (isEditing: boolean) => void;
  buttonSize?: IconSizeType;
};

const SELECTED_COLORING_CLASSES: Record<
  ReturnType<typeof useSelectedPageListItemColoring>,
  string
> = {
  "selected-blue": "group-data-[selected]/listitem:bg-blue",
  "selected-green": "group-data-[selected]/listitem:bg-green-shade-20",
  "selected-orange": "group-data-[selected]/listitem:bg-orange",
};

/*
 * A component used to display an editing toolbar when hovering over an element.
 * This component may need to work in conjunction with an editable field component
 * hence we use a context here to manage the editing state.
 */
export const HoverableToolbar: FC<HoverableToolbarProps> = ({
  alwaysShowAllChildren = false,
  className,
  contentClassName,
  label,
  reference,
  readOnly,
  children: childrenProp,
  disableUnlessEditMode = false,
  uncentered = false,
  eventPrefix,
  onClickAdd,
  onClickDelete,
  onEditModeChange,
  showEditButton,
  buttonSize = "s",
}) => {
  const [isEditing, setIsEditingState] = useState(false);
  const [shouldStayOpen, setShouldStayOpen] = useState(/\btest\b/.test(contentClassName));
  const { isHovering, hoverParentProps } = useHoverable();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const shouldShowToolbar =
    !readOnly &&
    !isEditing &&
    (isHovering || shouldStayOpen) &&
    (!disableUnlessEditMode || isEditModeEnabled);
  const children = Children.toArray(childrenProp);

  // Using this function mainly to let callers know that we're editing
  // so they disable dragging and other functions we don't want while editing
  const setIsEditing = useCallback(
    (editing: boolean) => {
      setIsEditingState(editing);

      if (onEditModeChange) {
        onEditModeChange?.(editing);
      }
    },
    [setIsEditingState, onEditModeChange],
  );

  const contextValue = useMemo(() => {
    const setEditingIfAllowed = (editing: boolean) => {
      if (!readOnly) {
        setIsEditing(editing);
      }
    };

    return { isEditing, setIsEditing: setEditingIfAllowed, shouldStayOpen, setShouldStayOpen };
  }, [isEditing, setIsEditing, shouldStayOpen, setShouldStayOpen, readOnly]);

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const { presentedPageId } = usePresentation();
  const selectedListItemColoring = useSelectedPageListItemColoring();
  const selectedColor = presentedPageId ? selectedListItemColoring : "selected-blue";

  return (
    <HoverableToolbarContext.Provider value={contextValue}>
      {/* Using role=toolbar here to represent this as an object that generates a toolbar on hover
         Mainly we use this to simulate the hovering toolbar behavior in the tests */}
      <div
        className={clsx("hoverable-toolbar", className)}
        {...hoverParentProps}
        aria-label={label}
        role="toolbar"
      >
        <div className="relative flex w-full min-w-12 items-start justify-between">
          <div className="min-w-0 flex-1">{children[0]}</div>
          <div
            className={twMerge(
              "absolute right-0 ml-2 flex items-center",
              uncentered ? "h-[unset]" : "h-full",
              shouldShowToolbar ? SELECTED_COLORING_CLASSES[selectedColor] || "" : "",
              shouldShowToolbar ? contentClassName : "",
            )}
          >
            {shouldShowToolbar && onClickAdd && (
              <ToolbarAddIconButton
                eventPrefix={eventPrefix}
                reference={reference as AddIconButtonProps["reference"]}
                size={buttonSize}
                onClick={onClickAdd}
              />
            )}
            {shouldShowToolbar && onClickDelete && (
              <ToolbarDeleteIconButton
                eventPrefix={eventPrefix}
                reference={reference as DeleteIconButtonProps["reference"]}
                size={buttonSize}
                onClick={onClickDelete}
              />
            )}
            {shouldShowToolbar && showEditButton && (
              <ToolbarEditIconButton
                eventPrefix={eventPrefix}
                reference={reference as EditIconButtonProps["reference"]}
                size={buttonSize}
                onClick={onClickEdit}
              />
            )}
            {(shouldShowToolbar || (alwaysShowAllChildren && isHovering)) &&
              children.length > 1 && (
                <div
                  className="flex flex-row items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {children.slice(1)}
                </div>
              )}
          </div>
        </div>
      </div>
    </HoverableToolbarContext.Provider>
  );
};
